import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { SelectSearch } from "@/components/select-search";
import { cn } from "@/lib/utils";
import type { AvailableChild } from "nautika-types";

export function AddChildCombobox({
  targetGroupId,
  availableChildren,
  onChildAdd,
  excludeIds = [],
  onCreateChild,
}: {
  targetGroupId?: string;
  availableChildren: AvailableChild[];
  onChildAdd: (childId: string) => void;
  /** Liste des IDs d'enfants déjà inscrits à exclure de la liste */
  excludeIds?: string[];
  /** Callback pour créer un nouvel enfant avec groupe pré-rempli */
  onCreateChild?: (groupId?: string) => void;
  triggerClassName?: string;
}) {
  // État de recherche reçu depuis SelectSearch
  const [searchTerm, setSearchTerm] = useState("");

  // Appliquer excludeIds d'abord
  const filteredAllChildren = useMemo(
    () => (excludeIds.length > 0 ? availableChildren.filter((c) => !excludeIds.includes(c.id)) : availableChildren),
    [availableChildren, excludeIds]
  );

  // Enfants du groupe cible uniquement (pour l'affichage quand la recherche est vide)
  const groupOnlyChildren = useMemo(() => filteredAllChildren.filter((child) => child.groupId === targetGroupId), [filteredAllChildren, targetGroupId]);

  const handleSelect = (child: (typeof availableChildren)[0]) => {
    onChildAdd(child.id);
  };

  const handleCreateNew = () => {
    onCreateChild?.(targetGroupId);
  };

  // Fonction de filtrage personnalisée
  const filterChildren = (items: typeof filteredAllChildren, searchTerm: string) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return items; // items sera déjà limité au groupe si besoin
    // Sinon, chercher dans tous les enfants disponibles (items est l'ensemble complet quand on tape)
    return items.filter((c) => {
      const hay = `${c.firstName} ${c.lastName} ${c.age} ${c.groupName || ""}`.toLowerCase();
      return hay.includes(term);
    });
  };

  // Rendu personnalisé pour chaque enfant
  const renderChildItem = (child: (typeof availableChildren)[0]) => (
    <div>
      <p className="font-medium">
        {child.firstName} {child.lastName} · {child.age} ans
      </p>
      <p className="text-xs text-gray-500">{child.groupName}</p>
    </div>
  );

  // Rendu personnalisé du trigger
  const renderTrigger = () => (
    <div className="group relative h-full w-full">
      <button
        type="button"
        className="flex h-full min-h-[64px] w-full items-center justify-start rounded-lg border-2 border-dashed border-gray-300 px-4 py-4 text-sm font-medium text-muted-foreground transition hover:cursor-pointer hover:bg-muted/40"
      >
        <Plus className="mr-2 h-4 w-4" /> Ajouter un enfant
      </button>
      <div
        className={cn(
          "absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-green-500 text-white shadow-md",
          "pointer-events-none opacity-0 transition-opacity group-hover:opacity-100"
        )}
      >
        <Plus strokeWidth={3} className="h-3.5 w-3.5" />
      </div>
    </div>
  );

  // Trouver le nom du groupe cible
  const targetGroupName = useMemo(() => {
    if (!targetGroupId) return "";
    const childInGroup = availableChildren.find((c) => c.groupId === targetGroupId);
    return childInGroup?.groupName || "";
  }, [targetGroupId, availableChildren]);

  const emptyMessage = targetGroupName ? `Aucun enfant trouvé dans ${targetGroupName}` : "Aucun enfant trouvé";

  return (
    <SelectSearch
      // Si la recherche est vide -> n'afficher que les enfants du groupe.
      // Si une recherche est saisie -> permettre la recherche sur tous les enfants.
      items={searchTerm.trim() ? filteredAllChildren : groupOnlyChildren}
      onItemSelect={handleSelect}
      onCreateNew={handleCreateNew}
      onSearch={setSearchTerm}
      filterFunction={filterChildren}
      renderItem={renderChildItem}
      renderTrigger={renderTrigger}
      placeholder="Rechercher un enfant..."
      createNewText="Créer un nouvel enfant"
      emptyMessage={emptyMessage}
      width="w-[320px]"
      popoverAlign="start"
      showCreateButton={true}
      closeOnSelect={true}
      isMultiSelect={false}
      modal={true}
    />
  );
}
